import React, { useState, useMemo, useEffect } from "react";

import {
  SquarePen,
  Trash2,
  Building2,
  User,
  Users,
  AlertTriangle,
  Download,
  FileSpreadsheet,
  FileText,
} from "lucide-react";

import Accordion from "../../../Components/Accordion";
import Button from "../../../Components/Button";
import DropdownSelector from "../../../Components/DropdownSelector";
import Input from "../../../Components/Input";
import Modal from "../../../Components/Modal";
import RadioButton from "../../../Components/RadioButton";
import SearchInput from "../../../Components/Search/SearchInput";
import Select from "../../../Components/Select";
import SuccessNotification from "../../../Components/SuccessNotification";
import Table from "../../../Components/Table";
import TextArea from "../../../Components/TextArea";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchRecommendations as fetchRecommendationsAction,
  clearRecommendations,
  editAssignment,
  clearEditAssignment,
  deleteAssignment,
  clearDeleteAssignment,
  fetchAssignments,
  ExecutiveFromAPI,
  exportStakeholders,
} from "../../../Store/Reducers/AssignmentSlice";
import { fetchAdminUsers } from "../../../Store/Reducers/MasterSlice";
import { formatCompactCurrency } from "../../../Utils/Helper/formatCompactCurrency";
import { toast } from "../../../Utils/Helper/toast";
import {
  ExecutiveForUI,
  SecondaryExecutiveSelector,
  AssignmentNotes,
  BatchApproverForm,
  ExecutiveList,
  mapExecutiveToUI,
  AccountDetails,
  UnassignedAccount,
  AIRecommendationsBanner,
  buildApproverSelectOptions,
  CapacityUtilizationBar,
  AccountSummaryRow,
  getDefaultEditBatchName,
} from "../SharedComponents";

import "./style.scss";

type AssignmentRecord = {
  secondaryExecutiveSubtitle: any;
  id: string | null;
  accountId: string | null;
  accountName: string;
  accountSubtitle: string;
  primaryExecutive: string | undefined;
  primaryExecutiveId: string | null;
  primaryExecutiveSubtitle: string;
  secondaryExecutive: string | undefined;
  secondaryExecutiveId: string | null;
  assignedOn: string;
  status: string;
  assignmentPriority: string | null;
  relationshipType: string | null;
  batchInfo: string;
  batchName: string;
  assignmentMode: string;
  notes: string;
  actions: string;
  fullAssignment?: any;
};

type Props = {
  assignmentsData: AssignmentRecord[];
  searchQuery: string;
  setSearchQuery: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRefreshCounts?: () => void;
};

const ExecutiveProfileCell = ({
  name,
  subtitle,
  variant = "primary",
  extra,
}: {
  name?: string;
  subtitle?: string;
  variant?: "primary" | "secondary";
  extra?: React.ReactNode;
}) => {
  if (!name) {
    return <span className="text-gray-400 italic">None</span>;
  }

  const iconStyles =
    variant === "primary"
      ? { bg: "bg-emerald-100", icon: "text-emerald-600" }
      : { bg: "bg-purple-100", icon: "text-purple-600" };

  return (
    <div className="flex items-center gap-2.5 w-full min-w-0">
      <div
        className={`flex-shrink-0 w-9 h-9 rounded-full ${iconStyles.bg} flex items-center justify-center`}
      >
        <Users className={`w-4 h-4 ${iconStyles.icon}`} strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="text-sm font-medium text-gray-900 break-words leading-tight">
          {name}
        </div>
        {subtitle ? (
          <div className="text-sm text-gray-500 break-words leading-snug mt-0.5">
            {subtitle}
          </div>
        ) : null}
        {extra}
      </div>
    </div>
  );
};

const AllAssignmentContainer: React.FC<Props> = ({
  assignmentsData,
  searchQuery,
  setSearchQuery,
  onRefreshCounts,
}) => {
  const dispatch = useAppDispatch();
  const stakeholdersExportLoading = useAppSelector(
    state => state.assignments.stakeholdersExport.loading
  );

  const handleExportStakeholders = async (format: "csv" | "excel") => {
    try {
      const filename = await dispatch(exportStakeholders(format)).unwrap();
      toast.success(`Exported ${filename}`);
    } catch {
      // AssignmentSlice shows error toast on rejection
    }
  };

  // State for removal modal
  const [showRemovalModal, setShowRemovalModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<AssignmentRecord | null>(null);
  const [removalBatchName, setRemovalBatchName] = useState("");
  const [selectedApprover, setSelectedApprover] = useState<string | null>(null);
  const [removalNotes, setRemovalNotes] = useState("");

  // State for edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStep, setEditStep] = useState(1); // 1 = select, 2 = review changes, 3 = final review
  const [editBatchName, setEditBatchName] = useState("");
  const [editSelectedApprover, setEditSelectedApprover] = useState<
    string | null
  >(null);
  const [editPrimaryExecutive, setEditPrimaryExecutive] = useState<
    string | null
  >(null);
  const [editSecondaryExecutive, setEditSecondaryExecutive] = useState<
    string | null
  >(null);
  const [editAssignmentNotes, setEditAssignmentNotes] = useState("");
  const [editAssignMode, setEditAssignMode] = useState<"manual" | "auto">(
    "manual"
  );
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successNotificationType, setSuccessNotificationType] = useState<
    "edit" | "delete" | null
  >(null);

  // Local UI state for executives
  const [executives, setExecutives] = useState<ExecutiveForUI[]>([]);
  const [secondaryExecutives, setSecondaryExecutives] = useState<
    ExecutiveForUI[]
  >([]);

  // Redux state
  const {
    data: recommendationsData,
    loading: loadingRecommendations,
    error: recommendationsError,
  } = useAppSelector(state => state.assignments.recommendations);

  const {
    loading: editingAssignment,
    error: editAssignmentError,
    success: editAssignmentSuccess,
  } = useAppSelector(state => state.assignments.editAssignment);

  const {
    loading: deletingAssignment,
    error: deleteAssignmentError,
    success: deleteAssignmentSuccess,
  } = useAppSelector(state => state.assignments.deleteAssignment);

  const {
    items: adminUsers,
    loading: adminUsersLoading,
    error: adminUsersError,
  } = useAppSelector(state => state.master.adminUsers);

  const COLOR_LEGEND_ITEMS = [
    {
      label: "Selected",
      subtext: "Current choice",
      border: "border border-purple-500",
    },
    {
      label: "High Match",
      subtext: "80%+ score",
      border: "border border-green-300",
    },
    {
      label: "Medium Match",
      subtext: "60-79% score",
      border: "border border-yellow-300",
    },
    {
      label: "Low Match",
      subtext: "<60% score",
      border: "border border-gray-300",
    },
  ] as const;

  const handleRemovalRequest = (assignment: AssignmentRecord) => {
    setSelectedAssignment(assignment);
    setRemovalBatchName(
      `Removal: ${assignment.accountName} - ${new Date().toISOString().split("T")[0]}`
    );
    setSelectedApprover(null);
    setRemovalNotes("");
    setShowRemovalModal(true);

    // Fetch admin users if not loaded
    if (adminUsers.length === 0 && !adminUsersLoading) {
      dispatch(fetchAdminUsers());
    }
  };

  const handleRemovalSubmit = async () => {
    if (
      !selectedAssignment ||
      !selectedAssignment.accountId ||
      !selectedAssignment.primaryExecutiveId ||
      !removalBatchName ||
      !selectedApprover
    ) {
      return;
    }

    const payload = {
      accountId: selectedAssignment.accountId,
      accountName: selectedAssignment.accountName,
      primaryExecutiveId: selectedAssignment.primaryExecutiveId,
      secondaryExecutiveIds: selectedAssignment.secondaryExecutiveId
        ? [selectedAssignment.secondaryExecutiveId]
        : [],
      approverId: selectedApprover,
      batchName: removalBatchName,
      notes: removalNotes || undefined,
    };

    await dispatch(deleteAssignment(payload));
  };

  // Map admin users to approver options: "Name — email", grouped by role
  const { ungroupedOptions, groups: approverGroups } = useMemo(
    () => buildApproverSelectOptions(adminUsers),
    [adminUsers]
  );
  const approverOptions = ungroupedOptions;

  // Fetch recommendations
  const fetchRecommendations = async (
    accountId: string,
    mode: "auto" | "manual"
  ) => {
    if (!accountId) {
      return;
    }
    await dispatch(
      fetchRecommendationsAction({
        accountIds: [accountId],
        mode,
      })
    );
  };

  // Handle edit button click
  const handleEditRequest = async (assignment: AssignmentRecord) => {
    if (!assignment.accountId) {
      return;
    }

    const mode = (assignment.assignmentMode === "auto" ? "auto" : "manual") as
      | "auto"
      | "manual";

    setSelectedAssignment(assignment);
    setShowEditModal(true);
    setEditStep(1);
    setEditBatchName("");
    setEditSelectedApprover(null);
    setEditPrimaryExecutive(assignment.primaryExecutiveId);
    setEditSecondaryExecutive(assignment.secondaryExecutiveId);
    // setEditAssignmentNotes(assignment.notes || "");
    setEditAssignMode(mode);
    setExecutives([]);
    setSecondaryExecutives([]);

    // Fetch admin users if not loaded
    if (adminUsers.length === 0 && !adminUsersLoading) {
      dispatch(fetchAdminUsers());
    }

    // Fetch recommendations
    await fetchRecommendations(assignment.accountId, mode);
  };

  // Transform API response to UI format when recommendations data changes
  useEffect(() => {
    if (recommendationsData && selectedAssignment) {
      const response = recommendationsData;
      const mode = response.mode || editAssignMode;
      const primarySponsorArray = response.bulk?.primary_sponsor ?? [];

      // Collect all unique executives: primary_sponsor + secondary_sponsors
      const allExecutivesMap = new Map<string, ExecutiveFromAPI>();
      if (
        Array.isArray(primarySponsorArray) &&
        primarySponsorArray.length > 0
      ) {
        primarySponsorArray.forEach((s: ExecutiveFromAPI) =>
          allExecutivesMap.set(s.id, s)
        );
      }
      if (response.bulk?.secondary_sponsors?.length) {
        response.bulk.secondary_sponsors.forEach((s: ExecutiveFromAPI) => {
          if (!allExecutivesMap.has(s.id)) allExecutivesMap.set(s.id, s);
        });
      }

      const allExecutives = Array.from(allExecutivesMap.values()).map(
        mapExecutiveToUI
      );

      // Auto mode: use primary_sponsor array; manual: use all executives
      // Pin-to-top for current selection is handled by ExecutiveList/SecondaryExecutiveSelector via initialSelectedId
      if (mode === "auto") {
        const arr = Array.isArray(primarySponsorArray)
          ? primarySponsorArray
          : [];
        setExecutives(arr.map(mapExecutiveToUI));
      } else {
        setExecutives(allExecutives);
      }

      if (response.bulk?.secondary_sponsors?.length) {
        setSecondaryExecutives(
          response.bulk.secondary_sponsors.map(mapExecutiveToUI)
        );
      } else {
        const primaryIds = new Set(
          Array.isArray(primarySponsorArray)
            ? primarySponsorArray.map((p: ExecutiveFromAPI) => p.id)
            : []
        );
        setSecondaryExecutives(
          allExecutives.filter(e => !primaryIds.has(e.id))
        );
      }
    } else {
      setExecutives([]);
      setSecondaryExecutives([]);
    }
  }, [recommendationsData, editAssignMode, selectedAssignment]);

  // Fetch admin users when edit modal opens
  useEffect(() => {
    if (showEditModal && adminUsers.length === 0 && !adminUsersLoading) {
      dispatch(fetchAdminUsers());
    }
  }, [showEditModal, dispatch, adminUsers.length, adminUsersLoading]);

  // Default batch name when reaching edit step 2: "Edit Assignment - {account} - {today}"
  useEffect(() => {
    if (
      showEditModal &&
      editStep === 2 &&
      selectedAssignment &&
      !editBatchName.trim()
    ) {
      setEditBatchName(getDefaultEditBatchName(selectedAssignment.accountName));
    }
  }, [
    showEditModal,
    editStep,
    selectedAssignment?.accountId,
    selectedAssignment?.accountName,
  ]);

  // Handle edit submission
  const handleEditSubmit = async () => {
    if (
      !selectedAssignment ||
      !selectedAssignment.accountId ||
      !editPrimaryExecutive ||
      !editBatchName ||
      !editSelectedApprover
    ) {
      return;
    }

    const secondaryId =
      editSecondaryExecutive && editSecondaryExecutive !== editPrimaryExecutive
        ? editSecondaryExecutive
        : null;

    const payload = {
      accountId: selectedAssignment.accountId,
      accountName: selectedAssignment.accountName,
      primaryExecutiveId: editPrimaryExecutive,
      secondaryExecutiveIds: secondaryId ? [secondaryId] : [],
      notes: editAssignmentNotes || undefined,
      assignmentMode: editAssignMode,
      batchName: editBatchName,
      approverId: editSelectedApprover,
    };

    await dispatch(editAssignment(payload));
  };

  // Handle success after delete submission
  useEffect(() => {
    if (deleteAssignmentSuccess) {
      setSuccessNotificationType("delete");
      setShowSuccessNotification(true);
      setShowRemovalModal(false);
      setSelectedAssignment(null);
      setRemovalBatchName("");
      setSelectedApprover(null);
      setRemovalNotes("");
      dispatch(clearDeleteAssignment());
      if (onRefreshCounts) onRefreshCounts();
      else dispatch(fetchAssignments());
      setTimeout(() => {
        dispatch(clearDeleteAssignment());
      }, 100);
    }
  }, [deleteAssignmentSuccess, dispatch]);

  // Handle success after edit submission
  useEffect(() => {
    if (editAssignmentSuccess) {
      setSuccessNotificationType("edit");
      setShowSuccessNotification(true);
      setShowEditModal(false);
      setEditStep(1);
      setEditBatchName("");
      setEditSelectedApprover(null);
      setEditPrimaryExecutive(null);
      setEditSecondaryExecutive(null);
      setEditAssignmentNotes("");
      setSelectedAssignment(null);
      setExecutives([]);
      setSecondaryExecutives([]);
      dispatch(clearRecommendations());
      dispatch(clearEditAssignment());
      if (onRefreshCounts) onRefreshCounts();
      else dispatch(fetchAssignments());
      setTimeout(() => {
        dispatch(clearEditAssignment());
      }, 100);
    }
  }, [editAssignmentSuccess, dispatch]);

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditStep(1);
    setEditBatchName("");
    setEditSelectedApprover(null);
    setEditPrimaryExecutive(null);
    setEditSecondaryExecutive(null);
    setEditAssignmentNotes("");
    setSelectedAssignment(null);
    setExecutives([]);
    setSecondaryExecutives([]);
    dispatch(clearRecommendations());
    dispatch(clearEditAssignment());
  };

  const goEditNext = () => setEditStep(s => Math.min(2, s + 1));
  const goEditBack = () => setEditStep(s => Math.max(1, s - 1));

  // Filter assignments by Account, Primary Executive, and Secondary Executive
  const filteredAssignmentsData = useMemo(() => {
    if (!searchQuery.trim()) {
      return assignmentsData;
    }

    const query = searchQuery.toLowerCase().trim();
    return assignmentsData.filter(
      assignment =>
        assignment?.accountName?.toLowerCase().includes(query) ||
        assignment?.primaryExecutive?.toLowerCase().includes(query) ||
        assignment?.secondaryExecutive?.toLowerCase().includes(query) ||
        String(assignment?.secondaryExecutiveSubtitle ?? "")
          .toLowerCase()
          .includes(query)
    );
  }, [assignmentsData, searchQuery]);

  return (
    <div className="bg-white rounded-lg border p-3">
      <div className="assignment-toolbar mb-5 flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <SearchInput
            placeholder="Search"
            value={searchQuery}
            onChange={setSearchQuery}
            rounded="normal"
          />
        </div>
        <DropdownSelector
          label={stakeholdersExportLoading ? "Exporting..." : "Export"}
          icon={<Download size={15} strokeWidth={2} />}
          triggerStyle="button"
          loading={stakeholdersExportLoading}
          selected={null}
          options={[
            {
              label: "CSV",
              value: "csv",
              icon: <FileText size={14} strokeWidth={2} />,
              iconClassName: "dropdown-selector-menu__icon--csv",
            },
            {
              label: "Excel",
              value: "excel",
              icon: <FileSpreadsheet size={14} strokeWidth={2} />,
              iconClassName: "dropdown-selector-menu__icon--excel",
            },
          ]}
          onSelect={value => {
            if (value === "csv" || value === "excel") {
              void handleExportStakeholders(value);
            }
          }}
        />
      </div>
      <div className="assignment-table">
        <Table
          className="assignment-table-scroll"
          sortable
          columns={[
            {
              key: "account",
              title: "Account",
              width: "280px",
              sortable: true,
              sortValue: (record: AssignmentRecord) => record.accountName,
              render: (_: any, record: AssignmentRecord) => (
                <div className="flex items-center gap-2.5 w-full min-w-0">
                  <div className="flex-shrink-0">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                      <Building2
                        className="w-4 h-4 text-blue-600"
                        strokeWidth={2}
                      />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="fw-500 font-medium break-words">
                      {record.accountName}
                    </div>
                    <div className="text-sm text-gray-500 break-words">
                      {record.accountSubtitle}
                    </div>
                    {record.batchInfo && (
                      <div className="text-sm text-gray-500 break-words">
                        {record.batchInfo}
                      </div>
                    )}
                  </div>
                </div>
              ),
            },
            {
              key: "primaryExecutive",
              title: "Primary Executive",
              width: "280px",
              sortable: true,
              sortValue: (record: AssignmentRecord) =>
                record.primaryExecutive || "",
              render: (_: any, record: AssignmentRecord) => (
                <ExecutiveProfileCell
                  name={record.primaryExecutive}
                  subtitle={record.primaryExecutiveSubtitle}
                  variant="primary"
                  extra={
                    record.relationshipType ? (
                      <div className="text-xs text-gray-400 break-words mt-0.5">
                        Type: {record.relationshipType}
                      </div>
                    ) : null
                  }
                />
              ),
            },
            {
              key: "secondaryExecutive",
              title: "Secondary Executive",
              width: "280px",
              sortable: true,
              sortValue: (record: AssignmentRecord) =>
                record.secondaryExecutive || "",
              render: (_: any, record: AssignmentRecord) => (
                <ExecutiveProfileCell
                  name={record.secondaryExecutive}
                  subtitle={record.secondaryExecutiveSubtitle}
                  variant="secondary"
                />
              ),
            },
            {
              key: "assignedOn",
              title: "Assigned On",
              width: "140px",
              className: "whitespace-nowrap",
              sortable: true,
              sortValue: (record: AssignmentRecord) =>
                record.fullAssignment?.created_at
                  ? new Date(record.fullAssignment.created_at).getTime()
                  : 0,
              render: (value: string) => <span>{value}</span>,
            },
            {
              key: "actions",
              title: "Actions",
              width: "220px",
              className: "whitespace-nowrap",
              textalign: "center",
              render: (_: string, record: AssignmentRecord) => (
                <div className="flex justify-center gap-2">
                  <Button
                    variant="clear"
                    className="font-normal border border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 flex items-center gap-2 px-3 py-2 rounded"
                    onClick={() => handleEditRequest(record)}
                    title="Edit"
                  >
                    <SquarePen size={18} />
                    <span>Edit</span>
                  </Button>
                  <Button
                    variant="clear"
                    className="font-normal border border-red-300 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 flex items-center gap-2 px-3 py-2 rounded"
                    onClick={() => handleRemovalRequest(record)}
                    title="Remove"
                  >
                    <Trash2 size={18} />
                    <span>Remove</span>
                  </Button>
                </div>
              ),
            },
          ]}
          data={filteredAssignmentsData}
          scrollable
          maxHeight="calc(100vh - 21.9rem)"
        />
      </div>

      {/* Removal Request Modal */}
      {showRemovalModal && selectedAssignment && (
        <Modal
          isOpen={showRemovalModal}
          toggle={() => setShowRemovalModal(false)}
          title="Request Assignment Removal"
          size="2xl"
        >
          <div>
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-50 ring-1 ring-red-100">
                <AlertTriangle
                  className="h-[22px] w-[22px] text-red-500"
                  strokeWidth={2}
                  aria-hidden="true"
                />
              </div>
              <div className="min-w-0">
                <h5 className="text-base font-semibold text-gray-900 m-0 leading-snug">
                  Assignment Details
                </h5>
                <p className="text-sm text-gray-500 m-0 mt-0.5 leading-snug">
                  This removal request requires approval
                </p>
              </div>
            </div>

            <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-gray-200">
                <div className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Building2 className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Account
                      </p>
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {selectedAssignment.accountName}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <User className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Executive
                      </p>
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {selectedAssignment.primaryExecutive}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <Input
                label="Batch Name"
                name="batchName"
                value={removalBatchName}
                onChange={e => setRemovalBatchName(e.target.value)}
                required
              />
            </div>

            <div className="mb-6">
              {adminUsersLoading ? (
                <div className="text-gray-600 text-sm">
                  Loading approvers...
                </div>
              ) : adminUsersError ? (
                <div className="text-red-600 text-sm">
                  Error loading approvers. Please try again.
                </div>
              ) : (
                <Select
                  name="approver"
                  label="Select Approver"
                  value={selectedApprover ?? ""}
                  onChange={e => setSelectedApprover(e.target.value || null)}
                  options={approverOptions}
                  groups={approverGroups}
                  placeholder="Choose an approver..."
                  required
                />
              )}
            </div>

            <div className="mb-6">
              <TextArea
                name="removalNotes"
                label="Notes (Optional)"
                value={removalNotes}
                placeholder="Optional notes about this removal request..."
                onChange={e => setRemovalNotes(e.target.value)}
                rows={4}
              />
            </div>

            <div
              className="mb-6 w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5"
              role="status"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle
                  className="h-4 w-4 shrink-0 text-amber-600"
                  strokeWidth={2}
                  aria-hidden="true"
                />
                <p className="m-0 text-sm font-semibold text-amber-900">
                  Important
                </p>
              </div>
              <p className="m-0 mt-1.5 text-sm leading-relaxed text-amber-800/90">
                This removal request will be sent to the selected approver for
                review. The assignment will remain active until approved. You
                can track its progress in the Approvals tab.
              </p>
            </div>

            {deleteAssignmentError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                <strong>Error:</strong> {deleteAssignmentError}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline-primary"
                onClick={() => {
                  setShowRemovalModal(false);
                  setSelectedAssignment(null);
                  setRemovalBatchName("");
                  setSelectedApprover(null);
                  setRemovalNotes("");
                  dispatch(clearDeleteAssignment());
                }}
                disabled={deletingAssignment}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleRemovalSubmit}
                disabled={
                  deletingAssignment ||
                  !removalBatchName ||
                  !selectedApprover ||
                  !selectedAssignment?.accountId ||
                  !selectedAssignment?.primaryExecutiveId
                }
              >
                {deletingAssignment
                  ? "Submitting..."
                  : "Submit Removal Request"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Assignment Modal */}
      {showEditModal && selectedAssignment && (
        <Modal
          isOpen={showEditModal}
          toggle={closeEditModal}
          title={`Edit Assignment - Step ${editStep}: ${
            editStep === 1 ? "Select" : "Review & Submit"
          }`}
          size="5xl"
          footer={
            editStep === 1 ? (
              <div className="flex flex-nowrap items-center justify-end gap-3">
                <Button variant="outline-primary" onClick={closeEditModal}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={goEditNext}
                  disabled={!editPrimaryExecutive}
                >
                  Review & Submit
                </Button>
              </div>
            ) : (
              <div className="flex flex-nowrap items-center justify-between gap-3">
                <Button variant="outline-primary" onClick={goEditBack}>
                  Back to Selection
                </Button>
                <div className="flex flex-col items-end gap-2">
                  <Button
                    variant="primary"
                    onClick={handleEditSubmit}
                    disabled={
                      editingAssignment ||
                      !editPrimaryExecutive ||
                      !editBatchName ||
                      !editSelectedApprover
                    }
                  >
                    {editingAssignment
                      ? "Submitting..."
                      : "Confirm & Submit for Approval"}
                  </Button>
                  {editAssignmentError && (
                    <p className="text-red-600 text-sm m-0 text-right max-w-md">
                      {editAssignmentError}
                    </p>
                  )}
                </div>
              </div>
            )
          }
        >
          <div className="px-0">
            {editStep === 1 && (
              <>
                {/* Assignment Mode */}
                <div className="mb-4 flex items-center gap-4">
                  <h6 className="text-base font-medium text-gray-900 tracking-wide">
                    Assignment Mode
                  </h6>
                  <div className="flex flex-wrap items-center gap-4">
                    <RadioButton
                      name="editAssignMode"
                      value="auto"
                      label="Auto Match"
                      checked={editAssignMode === "auto"}
                      onChange={async () => {
                        setEditAssignMode("auto");
                        if (selectedAssignment.accountId) {
                          await fetchRecommendations(
                            selectedAssignment.accountId,
                            "auto"
                          );
                          if (executives.length > 0) {
                            const best = [...executives].sort(
                              (a, b) => b.match - a.match
                            )[0];
                            setEditPrimaryExecutive(best?.id);
                            if (
                              best?.id &&
                              best.id === editSecondaryExecutive
                            ) {
                              setEditSecondaryExecutive(null);
                            }
                          }
                        }
                      }}
                    />
                    <RadioButton
                      name="editAssignMode"
                      value="manual"
                      label="Manual Select"
                      checked={editAssignMode === "manual"}
                      onChange={async () => {
                        setEditAssignMode("manual");
                        setEditPrimaryExecutive(null);
                        if (selectedAssignment.accountId) {
                          await fetchRecommendations(
                            selectedAssignment.accountId,
                            "manual"
                          );
                        }
                      }}
                    />
                  </div>
                </div>

                {editAssignMode === "auto" ? (
                  <AIRecommendationsBanner
                    tier={
                      selectedAssignment.fullAssignment?.account?.tier || null
                    }
                  />
                ) : (
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 p-3 rounded-xl mb-4">
                    <h6 className="text-sm font-semibold text-purple-900 mb-2 uppercase tracking-wide">
                      Manual Selection
                    </h6>
                    <p className="mb-0 text-gray-700 text-sm leading-relaxed font-normal">
                      Select multiple executives to compare and choose the best
                      fit.
                    </p>
                  </div>
                )}

                {/* Color Legend */}
                <div className="mb-4 p-3 border rounded-xl">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 m-0">
                    Color Legend
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {COLOR_LEGEND_ITEMS.map((item, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-center p-3 bg-white ${item.border} rounded-lg min-h-[4rem]`}
                      >
                        <div className="text-center">
                          <div className="font-semibold text-xs text-gray-900 mb-0.5">
                            {item.label}
                          </div>
                          <div className="text-xs text-gray-600 font-normal">
                            {item.subtext}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Account Details */}
                <div className="mb-6 p-3 rounded-xl border border-gray-200 bg-gray-50/50">
                  <h6 className="text-md font-semibold text-gray-900 mb-3 tracking-wide border-b pb-2">
                    {selectedAssignment.accountName}
                  </h6>
                  {(() => {
                    const accountData =
                      selectedAssignment.fullAssignment?.account || {};
                    const compatibleAccount: UnassignedAccount | null =
                      selectedAssignment.accountId
                        ? {
                            id: selectedAssignment.accountId,
                            name: selectedAssignment.accountName,
                            industry: accountData.industry || "",
                            location:
                              accountData.country || accountData.city || "",
                            tier: accountData.tier || "",
                            value: formatCompactCurrency(accountData.revenue, {
                              fallback: "",
                            }),
                            status: selectedAssignment.status || "",
                          }
                        : null;
                    return (
                      <AccountDetails
                        account={compatibleAccount}
                        showTitle={false}
                      />
                    );
                  })()}
                </div>

                <ExecutiveList
                  executives={executives}
                  selectedId={editPrimaryExecutive}
                  onSelect={id => {
                    setEditPrimaryExecutive(id);
                    if (id != null && id === editSecondaryExecutive) {
                      setEditSecondaryExecutive(null);
                    }
                  }}
                  mode={editAssignMode}
                  loading={loadingRecommendations}
                  error={recommendationsError}
                  initialSelectedId={
                    selectedAssignment.primaryExecutiveId ?? undefined
                  }
                  aiRecommendedId={
                    editAssignMode === "auto"
                      ? (recommendationsData?.bulk?.customers?.[0]
                          ?.primary_sponsor_id ?? null)
                      : null
                  }
                />

                {/* Secondary Executive */}
                <SecondaryExecutiveSelector
                  executives={secondaryExecutives}
                  selectedId={editSecondaryExecutive}
                  onSelect={setEditSecondaryExecutive}
                  excludeId={editPrimaryExecutive}
                  initialSelectedId={
                    selectedAssignment.secondaryExecutiveId ?? undefined
                  }
                />

                {/* Assignment Notes */}
                <AssignmentNotes
                  value={editAssignmentNotes}
                  onChange={setEditAssignmentNotes}
                  name="editAssignmentNotes"
                />
              </>
            )}

            {editStep === 2 && (
              <>
                <div className="mb-4">
                  <h4 className="text-base font-semibold text-gray-900 m-0 tracking-tight">
                    Review Assignment Summary
                  </h4>
                  <p className="text-gray-500 text-sm mt-1 mb-0">
                    Confirm account and executive assignments grouped by
                    sponsor.
                  </p>
                </div>
                <div className="space-y-3 mb-4">
                  {(() => {
                    const primaryExec =
                      executives.find(m => m.id === editPrimaryExecutive) ||
                      secondaryExecutives.find(
                        m => m.id === editPrimaryExecutive
                      );
                    const secondaryId =
                      editSecondaryExecutive &&
                      editSecondaryExecutive !== editPrimaryExecutive
                        ? editSecondaryExecutive
                        : null;
                    const secondaryExec = secondaryId
                      ? executives.find(m => m.id === secondaryId) ||
                        secondaryExecutives.find(m => m.id === secondaryId)
                      : null;

                    const compatibleAccount: UnassignedAccount | null =
                      selectedAssignment.accountId
                        ? {
                            id: selectedAssignment.accountId,
                            name: selectedAssignment.accountName,
                            industry:
                              selectedAssignment.fullAssignment?.account
                                ?.industry || "",
                            location:
                              selectedAssignment.fullAssignment?.account
                                ?.country ||
                              selectedAssignment.fullAssignment?.account
                                ?.city ||
                              "",
                            tier:
                              selectedAssignment.fullAssignment?.account
                                ?.tier || "",
                            value: formatCompactCurrency(
                              selectedAssignment.fullAssignment?.account
                                ?.revenue,
                              { fallback: "" }
                            ),
                            status: selectedAssignment.status || "",
                          }
                        : null;

                    const sponsors: Array<{
                      exec: ExecutiveForUI;
                      accounts: UnassignedAccount[];
                    }> = [];
                    if (primaryExec && compatibleAccount) {
                      sponsors.push({
                        exec: primaryExec,
                        accounts: [compatibleAccount],
                      });
                    }

                    return sponsors.map(({ exec, accounts }) => {
                      const currentAccounts = exec.currentAccounts ?? 0;
                      const accountLimit = exec.accountLimit ?? 1;
                      return (
                        <Accordion
                          key={exec.id}
                          defaultOpen={true}
                          className="border border-gray-200 shadow-sm bg-white overflow-hidden mb-0"
                          title={(() => {
                            const match = exec.match ?? 0;
                            const matchLevel =
                              match >= 80
                                ? "high"
                                : match >= 60
                                  ? "medium"
                                  : "low";
                            const matchColorClass =
                              matchLevel === "high"
                                ? "text-green-600"
                                : matchLevel === "medium"
                                  ? "text-yellow-600"
                                  : "text-gray-600";
                            return (
                              <div className="flex items-center gap-4 w-full text-left pr-1">
                                <div className="flex-shrink-0 w-11 h-11 rounded-full bg-green-500 flex items-center justify-center text-white ring-2 ring-white shadow-sm">
                                  <Users className="w-5 h-5" strokeWidth={2} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-gray-900 truncate">
                                      {exec.name}
                                    </span>
                                    <span className="inline-flex items-center gap-x-2.5 px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100">
                                      <span
                                        className={`text-xs font-medium ${matchColorClass}`}
                                      >
                                        Match: {match}%
                                      </span>
                                    </span>
                                    <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-700 uppercase tracking-wide">
                                      Primary Sponsor
                                    </span>
                                  </div>
                                  <div className="text-sm text-gray-600 truncate mt-1">
                                    {exec.title}
                                    {exec.region && ` • ${exec.region}`}
                                  </div>
                                </div>
                                <CapacityUtilizationBar
                                  active={currentAccounts}
                                  pending={accounts.length}
                                  total={accountLimit}
                                  compact
                                />
                              </div>
                            );
                          })()}
                        >
                          <div className="border-t border-gray-200 bg-white">
                            {accounts.map((account, idx) => (
                              <AccountSummaryRow
                                key={account.id}
                                account={account}
                                secondaryLabel={
                                  secondaryExec
                                    ? secondaryExec.name
                                    : "Not selected"
                                }
                                secondaryIsSelected={!!secondaryExec}
                                notes={editAssignmentNotes}
                                isLast={idx === accounts.length - 1}
                              />
                            ))}
                          </div>
                        </Accordion>
                      );
                    });
                  })()}
                </div>
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden mb-6">
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/60">
                    <h5 className="text-base font-semibold text-gray-900 m-0 tracking-tight">
                      Batch & Approver
                    </h5>
                    <p className="text-gray-600 text-sm leading-relaxed mt-1 mb-0">
                      Enter a batch name and select an approver, then review the
                      summary below before submitting.
                    </p>
                  </div>
                  <div className="p-5">
                    <BatchApproverForm
                      batchName={editBatchName}
                      onBatchNameChange={setEditBatchName}
                      selectedApprover={editSelectedApprover}
                      onApproverChange={setEditSelectedApprover}
                      approverOptions={approverOptions}
                      approverGroups={approverGroups}
                      adminUsersLoading={adminUsersLoading}
                      adminUsersError={adminUsersError}
                      batchNameName="editBatchName"
                      approverName="editApprover"
                    />
                  </div>
                </div>
                <div className="border border-gray-200 rounded-xl bg-amber-50/80 border-amber-200/50 px-4 py-3.5">
                  <p className="font-semibold text-amber-900 m-0 text-sm">
                    Please review carefully
                  </p>
                  <p className="text-amber-800/90 m-0 text-sm leading-relaxed mt-1">
                    This assignment edit will be sent to the selected approver
                    for review.
                  </p>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Success Notification */}
      <SuccessNotification
        isOpen={showSuccessNotification}
        onClose={() => {
          setShowSuccessNotification(false);
          setSuccessNotificationType(null);
        }}
        message={
          successNotificationType === "delete"
            ? "Assignment Removal Submitted Successfully"
            : "Assignment Edit Submitted Successfully"
        }
        details={
          successNotificationType === "delete"
            ? "The assignment removal request has been submitted for approval. You can track its progress in the Approvals tab."
            : "The assignment edit has been submitted for approval. You can track its progress in the Approvals tab."
        }
      />
    </div>
  );
};

export default AllAssignmentContainer;
